import type { TableView, TableViewPrefs } from './types';
import type { TableViewStore } from './viewStore';

/** The backend's limit of own views per table; "All" takes one of them. */
export const MAX_OWN_VIEWS = 50;

/**
 * Loads a user's views for a table. On the first visit — no views of their own; colleagues' shared ones
 * don't count, they are not the user's tabs — it creates the first one
 * ("Vše") and then one tab per filter the user saved in the old list (`importLegacy`), in their order,
 * after it. The old saved filters stay as they are: the legacy list still uses them.
 *
 * The import runs only while the user has no views of their own, so a deleted imported tab never comes back, and
 * it is best-effort: an unreadable source imports nothing, and a failed create ends the import with
 * what is there so far. "Vše" is created either way.
 *
 * Two loads can overlap: React StrictMode starts every effect twice in development, and two mounted
 * tables could share a key. Each would read an empty store and create its own first view, so the
 * user ends up with two "Vše". Overlapping calls for the same user and table therefore share one
 * load; the next call after it settles reads the store afresh.
 */
const inFlight = new Map<string, Promise<{ views: TableView[]; prefs: TableViewPrefs }>>();

export const loadOrCreateViews = (
  store: TableViewStore,
  key: { userId: string; tableKey: string },
  createFirst: () => TableView,
  importLegacy?: () => Promise<TableView[]>,
): Promise<{ views: TableView[]; prefs: TableViewPrefs }> => {
  const id = `${key.userId}::${key.tableKey}`;
  const pending = inFlight.get(id);
  if (pending) return pending;

  const load = (async () => {
    const { views, prefs } = await store.load(key.tableKey);
    // The backend's `editable` says whose it is; localStorage views go by the owner.
    if (views.some(view => (view.editable ?? view.ownerId === key.userId) === true)) return { views, prefs };
    // Created without success toasts: the user did not ask for them, the table just needs its tabs.
    const created = [await store.createView(createFirst(), { silent: true })];
    const legacy = importLegacy ? await importLegacy().catch((): TableView[] => []) : [];
    for (const draft of legacy.slice(0, MAX_OWN_VIEWS - 1)) {
      try {
        created.push(await store.createView(draft, { silent: true }));
      } catch {
        break;
      }
    }
    // The user's own tabs come first; colleagues' shared ones keep their place after them.
    const order = [...created.map(view => view.id), ...prefs.order.filter(id => !created.some(view => view.id === id))];
    const nextPrefs = await store.savePrefs({ ...prefs, order, activeViewId: created[0].id });
    return { views: [...created, ...views], prefs: nextPrefs };
  })().finally(() => inFlight.delete(id));

  inFlight.set(id, load);
  return load;
};
